/**
 * LightBurn LBRN File Generator
 *
 * Generates LBRN (XML) files for LightBurn laser software.
 * Mirrors the structure and patterns of xcsGenerator.ts.
 */

import type { TemplateShape, ShapeProcessSettings, ControllerType } from '../types/dbTypes';
import type { Font } from '../types/dbTypes';
import type { ExtendedField } from './svgGenerator';
import { calculateTextFitInfo, Font as TextGenFont } from './textGenerator';
import { ROBOTO_REGULAR_BASE64 } from '../utils/robotoFont';
import { escapeXml } from '../utils/xmlUtils';
import { parsePathCommands, type PathCommand } from '../utils/pathUtils';
// Note: Using LightBurn native Text shapes with Weld="1" for proper text welding.
// calculateTextFitInfo provides accurate glyph-based sizing AND positioning (same as XCS).

// Re-export for backwards compatibility
export { escapeXml, parsePathCommands, type PathCommand };

// ============================================================
// LIGHTBURN LAYER COLORS (C00-C29 + T1, T2)
// Official colors from LightBurn software
// Source: https://softsolder.com/2025/04/11/inkscape-lightburn-layer-color-palette/
// ============================================================
export const LIGHTBURN_LAYER_COLORS = [
    // Standard layers C00-C29
    { value: '#000000', label: 'C00' },  // Black
    { value: '#0000FF', label: 'C01' },  // Blue
    { value: '#FF0000', label: 'C02' },  // Red
    { value: '#00E000', label: 'C03' },  // Green
    { value: '#D0D000', label: 'C04' },  // Yellow/Olive
    { value: '#FF8000', label: 'C05' },  // Orange
    { value: '#00E0E0', label: 'C06' },  // Cyan
    { value: '#FF00FF', label: 'C07' },  // Magenta
    { value: '#B4B4B4', label: 'C08' },  // Gray
    { value: '#0000A0', label: 'C09' },  // Dark Blue
    { value: '#A00000', label: 'C10' },  // Dark Red
    { value: '#00A000', label: 'C11' },  // Dark Green
    { value: '#A0A000', label: 'C12' },  // Olive
    { value: '#C08000', label: 'C13' },  // Brown
    { value: '#00A0FF', label: 'C14' },  // Light Blue
    { value: '#A000A0', label: 'C15' },  // Purple
    { value: '#808080', label: 'C16' },  // Medium Gray
    { value: '#7D87B9', label: 'C17' },  // Slate Blue
    { value: '#BB7784', label: 'C18' },  // Dusty Rose
    { value: '#4A6FE3', label: 'C19' },  // Royal Blue
    { value: '#D33F6A', label: 'C20' },  // Raspberry
    { value: '#8CD78C', label: 'C21' },  // Light Green
    { value: '#F0B98D', label: 'C22' },  // Peach
    { value: '#F6C4E1', label: 'C23' },  // Light Pink
    { value: '#FA9ED4', label: 'C24' },  // Pink
    { value: '#500A78', label: 'C25' },  // Deep Purple
    { value: '#B45A00', label: 'C26' },  // Rust
    { value: '#004754', label: 'C27' },  // Dark Teal
    { value: '#86FA88', label: 'C28' },  // Bright Green
    { value: '#FFDB66', label: 'C29' },  // Gold
    // Tool layers
    { value: '#F36926', label: 'T1' },   // Orange (Tool 1)
    { value: '#0C96D9', label: 'T2' },   // Sky Blue (Tool 2)
] as const;

// ============================================================
// Types and Interfaces
// ============================================================

export interface LbrnBuildOptions {
    fonts?: Font[];
    processSettings?: Map<string, Map<string, ShapeProcessSettings>>;
    controllerType?: ControllerType;
    mirrorX?: boolean;
    mirrorY?: boolean;
}

// LightBurn CutSetting types
export type LbrnCutType = 'Cut' | 'Scan' | 'Image';

export interface LbrnCutSetting {
    // Core settings (required)
    type: LbrnCutType;
    index: number;
    name: string;
    minPower: number;
    maxPower: number;
    speed: number;          // mm/min for GCode, mm/s for DSP
    numPasses: number;
    priority: number;

    // Secondary laser (DSP/dual-laser machines)
    minPower2?: number;
    maxPower2?: number;

    // Z-Axis
    zOffset?: number;       // Z offset (mm)
    zPerPass?: number;      // Z step per pass (mm)

    // Laser Control
    frequency?: number;     // PWM frequency (Hz)
    overrideFrequency?: boolean;
    ppi?: number;           // Pulses per inch
    enablePpi?: boolean;
    enableLaser1?: boolean; // Primary laser
    enableLaser2?: boolean; // Secondary laser

    // Timing (Line mode)
    startDelay?: number;    // ms before cut
    endDelay?: number;      // ms after cut

    // Air Assist
    runBlower?: boolean;    // Enable air assist
    blowerSpeedPercent?: number;
    blowerSpeedOverride?: boolean;

    // Cut-Through (Line mode)
    enableCutThrough?: boolean;
    throughPower?: number;  // Cut-through power %
    throughPower2?: number; // Secondary laser cut-through

    // Kerf (Line mode)
    kerf?: number;          // Kerf offset (mm)

    // Overcut & Ramp (Line mode)
    overcut?: number;       // mm past endpoints
    rampLength?: number;    // mm for power ramp

    // Perforation (Line mode)
    perforate?: boolean;
    perfLen?: number;       // Cut length (mm)
    perfSkip?: number;      // Skip length (mm)

    // Dot Mode (Line mode)
    dotMode?: boolean;
    dotTime?: number;       // Dwell time (ms)
    dotSpacing?: number;    // Space between dots (mm)

    // Lead In/Out (Line mode)
    leadInType?: 'none' | 'line' | 'arc';
    leadInLength?: number;  // mm
    leadInAngle?: number;   // degrees
    leadOutType?: 'none' | 'line' | 'arc';
    leadOutLength?: number; // mm
    leadOutAngle?: number;  // degrees

    // Scan/Fill Settings (type='Scan' only)
    interval?: number;      // Line spacing (mm)
    bidir?: boolean;        // Bidirectional fill
    overscan?: number;      // Overscan (%)
    angle?: number;         // Scan angle (degrees)
    crossHatch?: boolean;   // Two-pass 90° rotated
    floodFill?: boolean;    // Snake-fill algorithm

    // Image Settings (type='Image' only)
    ditherMode?: string;    // stucki, floyd, bayer, dot, line, diamond

    // Output Control
    doOutput?: boolean;     // Enable output
    hide?: boolean;         // Hide in UI
}

export interface LbrnVertex {
    x: number;
    y: number;
    // Bezier control points (optional)
    c0x?: number;
    c0y?: number;
    c1x?: number;
    c1y?: number;
}

export interface LbrnPrimitive {
    type: 'L' | 'B' | 'Q';  // Line, Bezier, Quadratic
    p0: number;             // Start vertex index
    p1: number;             // End vertex index
}

export interface LbrnShape {
    type: 'Path' | 'Rect' | 'Ellipse' | 'Text' | 'Group';
    cutIndex: number;
    xform?: string;         // Transform matrix "1 0 0 1 tx ty"
    // Path-specific
    vertices?: LbrnVertex[];
    primitives?: LbrnPrimitive[];
    // Rect-specific
    width?: number;
    height?: number;
    cornerRadius?: number;
    // Text-specific
    font?: string;
    text?: string;
    fontSize?: number;
}

// ============================================================
// Constants
// ============================================================

export const LBRN_VERSION = '2.0.05';
export const LBRN_FORMAT_VERSION = '0';

// Default layer colors (for reference - LightBurn uses index-based layers)
export const LBRN_LAYER_COLORS = [
    '#000000', // Layer 0 - Black
    '#0000FF', // Layer 1 - Blue
    '#FF0000', // Layer 2 - Red
    '#00FF00', // Layer 3 - Green
    '#FFFF00', // Layer 4 - Yellow
    '#00FFFF', // Layer 5 - Cyan
    '#FF00FF', // Layer 6 - Magenta
];

// ============================================================
// XML Utility Functions
// ============================================================

// escapeXml moved to xmlUtils.ts - imported above

/**
 * Creates an XML element with attributes
 */
export function xmlElement(
    name: string,
    attributes: Record<string, string | number | boolean | undefined>,
    children?: string,
    selfClosing: boolean = false
): string {
    const attrs = Object.entries(attributes)
        .filter(([_, v]) => v !== undefined)
        .map(([k, v]) => `${k}="${escapeXml(String(v))}"`)
        .join(' ');

    const attrStr = attrs ? ` ${attrs}` : '';

    if (selfClosing && !children) {
        return `<${name}${attrStr}/>`;
    }

    return `<${name}${attrStr}>${children || ''}</${name}>`;
}

/**
 * Creates a Value element (LightBurn's common pattern)
 * Example: <index Value="0"/>
 */
export function xmlValue(name: string, value: string | number | boolean): string {
    return `<${name} Value="${escapeXml(String(value))}"/>`;
}

/**
 * Builds an XForm (transform) string for LightBurn
 * Format: "a b c d tx ty" where [a b c d] is the 2x2 matrix and [tx ty] is translation
 * Identity + translate: "1 0 0 1 tx ty"
 */
export function buildXForm(
    tx: number = 0,
    ty: number = 0,
    scaleX: number = 1,
    scaleY: number = 1,
    rotation: number = 0
): string {
    if (rotation === 0 && scaleX === 1 && scaleY === 1) {
        // Simple translation
        return `1 0 0 1 ${tx.toFixed(4)} ${ty.toFixed(4)}`;
    }

    // Full affine transform
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const a = scaleX * cos;
    const b = scaleX * sin;
    const c = -scaleY * sin;
    const d = scaleY * cos;

    return `${a.toFixed(6)} ${b.toFixed(6)} ${c.toFixed(6)} ${d.toFixed(6)} ${tx.toFixed(4)} ${ty.toFixed(4)}`;
}

// ============================================================
// Speed and Power Conversion
// ============================================================

/**
 * Converts speed from mm/s to the appropriate unit for the controller type
 * - GCode (GRBL): mm/min
 * - DSP (Ruida): mm/s
 */
export function convertSpeed(speedMmPerSec: number, controllerType: ControllerType): number {
    switch (controllerType) {
        case 'grbl':
            return speedMmPerSec * 60; // Convert to mm/min
        case 'dsp':
        case 'galvo':
        default:
            return speedMmPerSec; // Already mm/s
    }
}

/**
 * Calculates minPower based on controller type
 * - DSP controllers use minPower at corners (typically 10% of max)
 * - GCode controllers often use same min/max
 */
export function calculateMinPower(maxPower: number, controllerType: ControllerType): number {
    switch (controllerType) {
        case 'dsp':
            return Math.max(maxPower * 0.1, 1); // 10% of max, minimum 1%
        case 'grbl':
        case 'galvo':
        default:
            return maxPower; // Same as max for GCode
    }
}

/**
 * Converts density (lines/cm) to interval (mm spacing)
 */
export function densityToInterval(density: number): number {
    if (density <= 0) return 0.1; // Default 0.1mm
    return 10 / density; // lines/cm to mm
}

// ============================================================
// CutSetting Generation
// ============================================================

/**
 * Creates a CutSetting element for vector operations (Cut/Line)
 * In LightBurn, both score and cut use 'Cut' type - differentiated by power/speed settings
 * Score: lower power, faster speed (marks without cutting through)
 * Cut: higher power, slower speed (cuts through material)
 */
export function createVectorCutSetting(
    index: number,
    options: {
        power?: number;
        speed?: number;
        passes?: number;
        controllerType?: ControllerType;
        isCut?: boolean;
        priority?: number;  // Display order in LightBurn (optional - defaults to index)
    } = {}
): LbrnCutSetting {
    const controllerType = options.controllerType || 'grbl';
    const isCut = options.isCut ?? false;

    // Score defaults: lower power (20%), faster speed (150mm/s)
    // Cut defaults: higher power (80%), slower speed (50mm/s)
    const maxPower = options.power ?? (isCut ? 80 : 20);
    const speedMmSec = options.speed ?? (isCut ? 50 : 150);

    return {
        type: 'Cut', // LightBurn uses 'Cut' for all vector operations
        index,
        name: `C${String(index).padStart(2, '0')}`,
        minPower: calculateMinPower(maxPower, controllerType),
        maxPower,
        speed: convertSpeed(speedMmSec, controllerType),
        numPasses: options.passes ?? 1,
        priority: options.priority ?? index,  // Use saved priority if available
    };
}

/**
 * Creates a CutSetting element for fill/scan operations (Engrave)
 */
export function createScanCutSetting(
    index: number,
    options: {
        power?: number;
        speed?: number;
        passes?: number;
        density?: number;
        controllerType?: ControllerType;
        bidir?: boolean;
        priority?: number;  // Display order in LightBurn (optional - defaults to index)
    } = {}
): LbrnCutSetting {
    const controllerType = options.controllerType || 'grbl';
    const maxPower = options.power ?? 30;
    const speedMmSec = options.speed ?? 200;
    const density = options.density ?? 100; // lines/cm

    return {
        type: 'Scan',
        index,
        name: `C${String(index).padStart(2, '0')}`,
        minPower: maxPower, // Scan typically uses same min/max
        maxPower,
        speed: convertSpeed(speedMmSec, controllerType),
        numPasses: options.passes ?? 1,
        priority: options.priority ?? index,  // Use saved priority if available
        interval: densityToInterval(density),
        bidir: options.bidir ?? true,
        overscan: 2.5, // Default 2.5% overscan
    };
}

/**
 * Creates a CutSetting_Img element for Image/Bitmap operations
 * Used for raster image engraving
 */
export function createImageCutSetting(
    index: number,
    options: {
        power?: number;
        speed?: number;
        controllerType?: ControllerType;
        priority?: number;
        ditherMode?: string;  // stucki, floyd, bayer, dot, line, diamond
    } = {}
): LbrnCutSetting {
    const controllerType = options.controllerType || 'grbl';
    const maxPower = options.power ?? 20;
    const speedMmSec = options.speed ?? 100;

    return {
        type: 'Image',  // LightBurn uses 'Image' for CutSetting_Img
        index,
        name: `C${String(index).padStart(2, '0')}`,
        minPower: maxPower,
        maxPower,
        speed: convertSpeed(speedMmSec, controllerType),
        numPasses: 1,
        priority: options.priority ?? index,
        ditherMode: options.ditherMode ?? 'stucki',  // Default dither mode
    };
}

/**
 * Maps ShapeProcessSettings to LightBurn-specific LbrnCutSetting fields
 * This extracts the new LightBurn settings without modifying core XCS fields
 */
export function mapProcessSettingsToLbrn(
    settings: ShapeProcessSettings | undefined,
    isEngrave: boolean
): Partial<LbrnCutSetting> {
    if (!settings) return {};

    const result: Partial<LbrnCutSetting> = {};

    // Z-Axis
    if (settings.zOffset !== undefined) result.zOffset = settings.zOffset;
    if (settings.zPerPass !== undefined) result.zPerPass = settings.zPerPass;

    // Laser Control
    if (settings.frequency !== undefined) result.frequency = settings.frequency;
    if (settings.overrideFrequency !== undefined) result.overrideFrequency = settings.overrideFrequency;
    if (settings.ppi !== undefined) result.ppi = settings.ppi;
    if (settings.enablePpi !== undefined) result.enablePpi = settings.enablePpi;
    if (settings.enableLaser1 !== undefined) result.enableLaser1 = settings.enableLaser1;
    if (settings.enableLaser2 !== undefined) result.enableLaser2 = settings.enableLaser2;

    // Timing (Line mode only)
    if (!isEngrave) {
        if (settings.startDelay !== undefined) result.startDelay = settings.startDelay;
        if (settings.endDelay !== undefined) result.endDelay = settings.endDelay;
    }

    // Air Assist (default ON for safety)
    result.runBlower = settings.airAssist ?? true;
    if (settings.blowerSpeedPercent !== undefined) {
        result.blowerSpeedPercent = settings.blowerSpeedPercent;
        result.blowerSpeedOverride = true;
    }

    // Cut-Through (Line mode only)
    if (!isEngrave && settings.enableCutThrough) {
        result.enableCutThrough = true;
        if (settings.cutThroughPower !== undefined) result.throughPower = settings.cutThroughPower;
    }

    // Kerf (Line mode only, from existing kerfDistance if enabled)
    if (!isEngrave && settings.kerfEnabled && settings.kerfDistance !== undefined) {
        result.kerf = settings.kerfDistance;
    }

    // Overcut & Ramp
    if (settings.overcut !== undefined) result.overcut = settings.overcut;
    if (settings.rampLength !== undefined) result.rampLength = settings.rampLength;

    // Perforation (Line mode only)
    if (!isEngrave && settings.enablePerforation) {
        result.perforate = true;
        if (settings.perforationCutLength !== undefined) result.perfLen = settings.perforationCutLength;
        if (settings.perforationSkipLength !== undefined) result.perfSkip = settings.perforationSkipLength;
    }

    // Dot Mode (Line mode only)
    if (!isEngrave && settings.enableDotMode) {
        result.dotMode = true;
        if (settings.dotTime !== undefined) result.dotTime = settings.dotTime;
        if (settings.dotSpacing !== undefined) result.dotSpacing = settings.dotSpacing;
    }

    // Lead In/Out (Line mode only)
    if (!isEngrave) {
        if (settings.leadInType && settings.leadInType !== 'none') {
            result.leadInType = settings.leadInType;
            if (settings.leadInLength !== undefined) result.leadInLength = settings.leadInLength;
            if (settings.leadInAngle !== undefined) result.leadInAngle = settings.leadInAngle;
        }
        if (settings.leadOutType && settings.leadOutType !== 'none') {
            result.leadOutType = settings.leadOutType;
            if (settings.leadOutLength !== undefined) result.leadOutLength = settings.leadOutLength;
            if (settings.leadOutAngle !== undefined) result.leadOutAngle = settings.leadOutAngle;
        }
    }

    // Scan Settings (Fill mode only)
    if (isEngrave) {
        if (settings.scanAngle !== undefined) result.angle = settings.scanAngle;
        if (settings.crossHatch !== undefined) result.crossHatch = settings.crossHatch;
        if (settings.floodFill !== undefined) result.floodFill = settings.floodFill;
        if (settings.overscanPercent !== undefined) result.overscan = settings.overscanPercent;
    }

    // Output control
    if (settings.outputEnabled !== undefined) result.doOutput = settings.outputEnabled;
    if (settings.hide !== undefined) result.hide = settings.hide;

    // Layer priority (display order)
    if (settings.lbrnPriority !== undefined) result.priority = settings.lbrnPriority;

    return result;
}

/**
 * Serializes a CutSetting to XML with all LightBurn attributes
 */
export function serializeCutSetting(setting: LbrnCutSetting): string {
    const children: string[] = [
        xmlValue('index', setting.index),
        xmlValue('name', setting.name),
        xmlValue('minPower', setting.minPower),
        xmlValue('maxPower', setting.maxPower),
        xmlValue('speed', setting.speed),
        xmlValue('numPasses', setting.numPasses),
        xmlValue('priority', setting.priority),
    ];

    // Secondary laser power (DSP/dual-laser machines)
    if (setting.minPower2 !== undefined) {
        children.push(xmlValue('minPower2', setting.minPower2));
    }
    if (setting.maxPower2 !== undefined) {
        children.push(xmlValue('maxPower2', setting.maxPower2));
    }

    // Z-Axis
    if (setting.zOffset !== undefined && setting.zOffset !== 0) {
        children.push(xmlValue('zOffset', setting.zOffset));
    }
    if (setting.zPerPass !== undefined && setting.zPerPass !== 0) {
        children.push(xmlValue('zPerPass', setting.zPerPass));
    }

    // Laser Control
    if (setting.overrideFrequency && setting.frequency) {
        children.push(xmlValue('overrideFrequency', 1));
        children.push(xmlValue('frequency', setting.frequency));
    }
    if (setting.enablePpi && setting.ppi) {
        children.push(xmlValue('enablePpi', 1));
        children.push(xmlValue('ppi', setting.ppi));
    }
    if (setting.enableLaser1 !== undefined) {
        children.push(xmlValue('enableLaser1', setting.enableLaser1 ? 1 : 0));
    }
    if (setting.enableLaser2 !== undefined && setting.enableLaser2) {
        children.push(xmlValue('enableLaser2', setting.enableLaser2 ? 1 : 0));
    }

    // Timing (Line mode)
    if (setting.startDelay !== undefined && setting.startDelay !== 0) {
        children.push(xmlValue('startDelay', setting.startDelay));
    }
    if (setting.endDelay !== undefined && setting.endDelay !== 0) {
        children.push(xmlValue('endDelay', setting.endDelay));
    }

    // Air Assist
    if (setting.runBlower !== undefined) {
        children.push(xmlValue('runBlower', setting.runBlower ? 1 : 0));
    }
    if (setting.blowerSpeedOverride && setting.blowerSpeedPercent !== undefined) {
        children.push(xmlValue('blowerSpeedOverride', 1));
        children.push(xmlValue('blowerSpeedPercent', setting.blowerSpeedPercent));
    }

    // Cut-Through (Line mode)
    if (setting.enableCutThrough) {
        children.push(xmlValue('enableCutThrough', 1));
        if (setting.throughPower !== undefined) {
            children.push(xmlValue('throughPower', setting.throughPower));
        }
        if (setting.throughPower2 !== undefined) {
            children.push(xmlValue('throughPower2', setting.throughPower2));
        }
    }

    // Kerf (Line mode)
    if (setting.kerf !== undefined && setting.kerf !== 0) {
        children.push(xmlValue('kerf', setting.kerf));
    }

    // Overcut & Ramp
    if (setting.overcut !== undefined && setting.overcut !== 0) {
        children.push(xmlValue('overcut', setting.overcut));
    }
    if (setting.rampLength !== undefined && setting.rampLength !== 0) {
        children.push(xmlValue('rampLength', setting.rampLength));
    }

    // Perforation (Line mode)
    if (setting.perforate) {
        children.push(xmlValue('perforate', 1));
        if (setting.perfLen !== undefined) {
            children.push(xmlValue('perfLen', setting.perfLen));
        }
        if (setting.perfSkip !== undefined) {
            children.push(xmlValue('perfSkip', setting.perfSkip));
        }
    }

    // Dot Mode (Line mode)
    if (setting.dotMode) {
        children.push(xmlValue('dotMode', 1));
        if (setting.dotTime !== undefined) {
            children.push(xmlValue('dotTime', setting.dotTime));
        }
        if (setting.dotSpacing !== undefined) {
            children.push(xmlValue('dotSpacing', setting.dotSpacing));
        }
    }

    // Lead In/Out (Line mode)
    if (setting.leadInType && setting.leadInType !== 'none') {
        // LightBurn uses 0 for line, 1 for arc
        children.push(xmlValue('leadInType', setting.leadInType === 'line' ? 0 : 1));
        if (setting.leadInLength !== undefined) {
            children.push(xmlValue('leadInLength', setting.leadInLength));
        }
        if (setting.leadInAngle !== undefined) {
            children.push(xmlValue('leadInAngle', setting.leadInAngle));
        }
    }
    if (setting.leadOutType && setting.leadOutType !== 'none') {
        children.push(xmlValue('leadOutType', setting.leadOutType === 'line' ? 0 : 1));
        if (setting.leadOutLength !== undefined) {
            children.push(xmlValue('leadOutLength', setting.leadOutLength));
        }
        if (setting.leadOutAngle !== undefined) {
            children.push(xmlValue('leadOutAngle', setting.leadOutAngle));
        }
    }

    // Scan/Fill settings (type='Scan' only)
    if (setting.type === 'Scan') {
        if (setting.interval !== undefined) {
            children.push(xmlValue('interval', setting.interval));
        }
        if (setting.bidir !== undefined) {
            children.push(xmlValue('bidir', setting.bidir ? 1 : 0));
        }
        if (setting.overscan !== undefined) {
            children.push(xmlValue('overscan', setting.overscan));
        }
        if (setting.angle !== undefined && setting.angle !== 0) {
            children.push(xmlValue('angle', setting.angle));
        }
        if (setting.crossHatch) {
            children.push(xmlValue('crossHatch', 1));
        }
        if (setting.floodFill) {
            children.push(xmlValue('floodFill', 1));
        }
    }

    // Image settings (type='Image' only)
    if (setting.type === 'Image') {
        if (setting.ditherMode !== undefined) {
            children.push(xmlValue('ditherMode', setting.ditherMode));
        }
    }

    // Output control
    if (setting.doOutput !== undefined && !setting.doOutput) {
        children.push(xmlValue('doOutput', 0));
    }
    if (setting.hide) {
        children.push(xmlValue('hide', 1));
    }

    // Use CutSetting_Img for Image type, CutSetting for others
    const elementName = setting.type === 'Image' ? 'CutSetting_Img' : 'CutSetting';
    return xmlElement(elementName, { type: setting.type }, '\n        ' + children.join('\n        ') + '\n    ');
}

// ============================================================
// Shape Generation
// ============================================================

/**
 * Creates an LBRN Rect shape element
 */
export function createRectShape(
    x: number,
    y: number,
    width: number,
    height: number,
    cutIndex: number,
    cornerRadius: number = 0
): string {
    const centerX = x + width / 2;
    const centerY = y + height / 2;

    const attrs: Record<string, string | number> = {
        Type: 'Rect',
        CutIndex: cutIndex,
        W: width,
        H: height,
        Cr: cornerRadius,
    };

    const xform = `<XForm>${buildXForm(centerX, centerY)}</XForm>`;

    return xmlElement('Shape', attrs, '\n        ' + xform + '\n    ');
}

/**
 * Creates an LBRN Ellipse shape element
 * For circles, use rx = ry = radius
 */
export function createEllipseShape(
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    cutIndex: number
): string {
    const attrs: Record<string, string | number> = {
        Type: 'Ellipse',
        CutIndex: cutIndex,
        Rx: rx,
        Ry: ry,
    };

    // XForm for ellipse: scale factors and center position
    // Format: scaleX 0 0 scaleY centerX centerY
    // For unscaled ellipse at position, use: 1 0 0 1 cx cy
    const xform = `<XForm>1 0 0 1 ${cx.toFixed(4)} ${cy.toFixed(4)}</XForm>`;

    return xmlElement('Shape', attrs, '\n        ' + xform + '\n    ');
}

/**
 * Creates an LBRN Text shape element
 * Based on LightBurn's native Text shape format with proper attributes
 */
export function createTextShape(
    text: string,
    x: number,
    y: number,
    cutIndex: number,
    options: {
        fontFamily?: string;
        width?: number;
        height?: number;
        fontSize?: number;          // Explicit font size in mm
        scaledBounds?: { minY: number; maxY: number };  // Glyph bounds for positioning
    } = {}
): string {
    const fontFamily = options.fontFamily ?? 'Arial';
    const shapeWidth = options.width ?? 50;
    const shapeHeight = options.height ?? 20;

    // Calculate appropriate font size:
    // - If explicitly provided, use that
    // - Otherwise, use 70% of shape height to leave padding
    // - Cap at reasonable size (max 50mm) and minimum (2mm)
    let fontSize = options.fontSize ?? (shapeHeight * 0.7);
    fontSize = Math.max(2, Math.min(fontSize, 50));

    // LightBurn font format: "FontFamily,-1,ReferenceSize,5,Weight,0,0,0,0,0"
    // ReferenceSize: 4096 is LightBurn's standard high-resolution reference
    // Weight: 400 = normal, 700 = bold
    const fontStr = `${fontFamily},-1,4096,5,400,0,0,0,0,0`;

    const attrs: Record<string, string | number> = {
        Type: 'Text',
        CutIndex: cutIndex,
        Font: fontStr,
        Str: text,
        H: fontSize.toFixed(2),     // Font height in mm (not shape height!)
        LS: '0',                    // Letter spacing
        LnS: '0',                   // Line spacing
        Ah: '1',                    // Horizontal alignment (1 = center)
        Av: '2',                    // Vertical alignment (2 = BOTTOM) - text bottom at XForm Y
        Weld: '1',                  // Weld overlapping paths
    };

    // Position: horizontal center, vertical bottom-aligned
    // Match SVG generator approach: baseline = boxBottom - descent
    // This ensures text bottom (baseline + descent) is at shape bottom
    const centerX = x + shapeWidth / 2;

    // scaledBounds.maxY = descent (positive for descenders, ~0 for no descenders)
    // SVG formula: y = boxBottom - descent
    const descent = options.scaledBounds?.maxY ?? 0;
    const bottomY = y + shapeHeight - descent;

    const xform = `<XForm>${buildXForm(centerX, bottomY)}</XForm>`;

    return xmlElement('Shape', attrs, '\n        ' + xform + '\n    ');
}

/**
 * Creates an LBRN Bitmap shape for raster images
 *
 * LBRN Bitmap stores:
 * - W, H: Original image dimensions
 * - Data: Raw base64 (NO prefix)
 * - XForm: scaleX 0 0 scaleY centerX centerY
 *
 * @param rasterData - Base64 data URI (with data:image/... prefix)
 * @param x - Top-left X position
 * @param y - Top-left Y position
 * @param width - Display width
 * @param height - Display height
 * @param originalWidth - Original image pixel width
 * @param originalHeight - Original image pixel height
 * @param cutIndex - Layer index
 * @param imageSettings - Optional preserved image settings (gamma, contrast, brightness)
 */
export function createBitmapShape(
    rasterData: string,
    x: number,
    y: number,
    width: number,
    height: number,
    originalWidth: number,
    originalHeight: number,
    cutIndex: number,
    imageSettings?: { gamma?: number; contrast?: number; brightness?: number }
): string {
    // Remove data URI prefix to get raw base64
    const base64 = rasterData.includes(',') ? rasterData.split(',')[1] : rasterData;

    // Calculate scale factors
    const scaleX = width / originalWidth;
    const scaleY = height / originalHeight;

    // XForm tx,ty is CENTER of bitmap
    const centerX = x + width / 2;
    const centerY = y + height / 2;

    const attrs: Record<string, string | number> = {
        Type: 'Bitmap',
        CutIndex: cutIndex,
        W: originalWidth,
        H: originalHeight,
        // Image processing settings (preserved for round-trip)
        Gamma: imageSettings?.gamma ?? 1,
        Contrast: imageSettings?.contrast ?? 0,
        Brightness: imageSettings?.brightness ?? 0,
        // Enhancement settings (default values)
        EnhanceAmount: 0,
        EnhanceRadius: 0,
        EnhanceDenoise: 0,
        // Data attribute contains raw base64
        Data: base64,
    };

    // XForm: scaleX 0 0 scaleY centerX centerY
    const xform = `<XForm>${scaleX.toFixed(8)} 0 0 ${scaleY.toFixed(8)} ${centerX.toFixed(4)} ${centerY.toFixed(4)}</XForm>`;

    return xmlElement('Shape', attrs, '\n        ' + xform + '\n    ');
}

/**
 * Creates an LBRN Path shape from vertices and primitives
 */
export function createPathShape(
    vertices: LbrnVertex[],
    primitives: LbrnPrimitive[],
    cutIndex: number,
    xform?: string
): string {
    const attrs: Record<string, string | number> = {
        Type: 'Path',
        CutIndex: cutIndex,
    };

    const children: string[] = [];

    if (xform) {
        children.push(`<XForm>${xform}</XForm>`);
    }

    // Add vertices
    for (const v of vertices) {
        let vAttrs = `vx="${v.x.toFixed(4)}" vy="${v.y.toFixed(4)}"`;
        if (v.c0x !== undefined && v.c0y !== undefined) {
            vAttrs += ` c0x="${v.c0x.toFixed(4)}" c0y="${v.c0y.toFixed(4)}"`;
        }
        if (v.c1x !== undefined && v.c1y !== undefined) {
            vAttrs += ` c1x="${v.c1x.toFixed(4)}" c1y="${v.c1y.toFixed(4)}"`;
        }
        children.push(`<V ${vAttrs}/>`);
    }

    // Add primitives
    for (const p of primitives) {
        children.push(`<P T="${p.type}" p0="${p.p0}" p1="${p.p1}"/>`);
    }

    return xmlElement('Shape', attrs, '\n        ' + children.join('\n        ') + '\n    ');
}

// ============================================================
// SVG Path Parsing (for converting SVG paths to LBRN format)
// ============================================================
// PathCommand interface and parsePathCommands moved to pathUtils.ts - imported above

/**
 * Converts SVG path commands to LBRN vertices and primitives
 * Returns an array of subpaths, each as a separate path data object
 * This is necessary because LightBurn expects each closed contour as a separate Shape
 */
export function svgPathToLbrnPaths(
    d: string,
    offsetX: number = 0,
    offsetY: number = 0,
    scaleX: number = 1,
    scaleY: number = 1
): { vertices: LbrnVertex[]; primitives: LbrnPrimitive[] }[] {
    const commands = parsePathCommands(d);
    const allPaths: { vertices: LbrnVertex[]; primitives: LbrnPrimitive[] }[] = [];

    // Current subpath data
    let vertices: LbrnVertex[] = [];
    let primitives: LbrnPrimitive[] = [];
    let subpathStartIdx = 0; // Track where current subpath starts

    let currentX = 0;
    let currentY = 0;
    let startX = 0;
    let startY = 0;
    let lastControlX = 0;
    let lastControlY = 0;

    // Helper to scale a coordinate
    const scaleCoord = (val: number, scale: number, offset: number) => val * scale + offset;

    const addVertex = (x: number, y: number, c0x?: number, c0y?: number, c1x?: number, c1y?: number): number => {
        const v: LbrnVertex = {
            x: scaleCoord(x, scaleX, offsetX),
            y: scaleCoord(y, scaleY, offsetY)
        };

        // c0 = outgoing control point (for curve FROM this vertex)
        if (c0x !== undefined && c0y !== undefined) {
            v.c0x = scaleCoord(c0x, scaleX, offsetX);
            v.c0y = scaleCoord(c0y, scaleY, offsetY);
        }
        // c1 = incoming control point (for curve TO this vertex)
        if (c1x !== undefined && c1y !== undefined) {
            v.c1x = scaleCoord(c1x, scaleX, offsetX);
            v.c1y = scaleCoord(c1y, scaleY, offsetY);
        }

        vertices.push(v);
        return vertices.length - 1;
    };

    // Finalize current subpath and start a new one
    const finalizeSubpath = () => {
        if (vertices.length > 0 && primitives.length > 0) {
            allPaths.push({ vertices: [...vertices], primitives: [...primitives] });
        }
        vertices = [];
        primitives = [];
        subpathStartIdx = 0;
    };

    for (const cmd of commands) {
        const { type, values } = cmd;
        const isRelative = type === type.toLowerCase();
        const cmdType = type.toUpperCase();

        switch (cmdType) {
            case 'M': // MoveTo - starts a new subpath
                // If we have an existing subpath, finalize it
                if (vertices.length > 0) {
                    finalizeSubpath();
                }

                for (let i = 0; i < values.length; i += 2) {
                    let x = values[i];
                    let y = values[i + 1];

                    if (isRelative && i > 0) {
                        x += currentX;
                        y += currentY;
                    } else if (isRelative && i === 0) {
                        x += currentX;
                        y += currentY;
                    }

                    if (i === 0) {
                        startX = x;
                        startY = y;
                        subpathStartIdx = vertices.length;
                        addVertex(x, y);
                    } else {
                        // Implicit LineTo after first point
                        const prevIdx = vertices.length - 1;
                        const newIdx = addVertex(x, y);
                        primitives.push({ type: 'L', p0: prevIdx, p1: newIdx });
                    }

                    currentX = x;
                    currentY = y;
                }
                break;

            case 'L': // LineTo
                for (let i = 0; i < values.length; i += 2) {
                    let x = values[i];
                    let y = values[i + 1];

                    if (isRelative) {
                        x += currentX;
                        y += currentY;
                    }

                    const prevIdx = vertices.length - 1;
                    const newIdx = addVertex(x, y);
                    primitives.push({ type: 'L', p0: prevIdx, p1: newIdx });

                    currentX = x;
                    currentY = y;
                }
                break;

            case 'H': // Horizontal LineTo
                for (const val of values) {
                    let x = isRelative ? currentX + val : val;

                    const prevIdx = vertices.length - 1;
                    const newIdx = addVertex(x, currentY);
                    primitives.push({ type: 'L', p0: prevIdx, p1: newIdx });

                    currentX = x;
                }
                break;

            case 'V': // Vertical LineTo
                for (const val of values) {
                    let y = isRelative ? currentY + val : val;

                    const prevIdx = vertices.length - 1;
                    const newIdx = addVertex(currentX, y);
                    primitives.push({ type: 'L', p0: prevIdx, p1: newIdx });

                    currentY = y;
                }
                break;

            case 'C': // Cubic Bezier
                for (let i = 0; i < values.length; i += 6) {
                    let x1 = values[i];
                    let y1 = values[i + 1];
                    let x2 = values[i + 2];
                    let y2 = values[i + 3];
                    let x = values[i + 4];
                    let y = values[i + 5];

                    if (isRelative) {
                        x1 += currentX; y1 += currentY;
                        x2 += currentX; y2 += currentY;
                        x += currentX; y += currentY;
                    }

                    // For bezier, we store control points in the vertices
                    // LightBurn convention: c0 = outgoing control point, c1 = incoming control point
                    const prevIdx = vertices.length - 1;
                    // Update previous vertex with outgoing control point (c0)
                    // x1,y1 is the first control point (near the start of the curve)
                    if (prevIdx >= 0) {
                        vertices[prevIdx].c0x = scaleCoord(x1, scaleX, offsetX);
                        vertices[prevIdx].c0y = scaleCoord(y1, scaleY, offsetY);
                    }

                    // Add new vertex with incoming control point (c1)
                    // x2,y2 is the second control point (near the end of the curve)
                    const newIdx = addVertex(x, y, undefined, undefined, x2, y2);
                    primitives.push({ type: 'B', p0: prevIdx, p1: newIdx });

                    lastControlX = x2;
                    lastControlY = y2;
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'S': // Smooth Cubic Bezier
                for (let i = 0; i < values.length; i += 4) {
                    // First control point is reflection of previous
                    let x1 = currentX + (currentX - lastControlX);
                    let y1 = currentY + (currentY - lastControlY);
                    let x2 = values[i];
                    let y2 = values[i + 1];
                    let x = values[i + 2];
                    let y = values[i + 3];

                    if (isRelative) {
                        x2 += currentX; y2 += currentY;
                        x += currentX; y += currentY;
                    }

                    const prevIdx = vertices.length - 1;
                    // Update previous vertex with outgoing control point (c0)
                    if (prevIdx >= 0) {
                        vertices[prevIdx].c0x = scaleCoord(x1, scaleX, offsetX);
                        vertices[prevIdx].c0y = scaleCoord(y1, scaleY, offsetY);
                    }

                    // Add new vertex with incoming control point (c1)
                    const newIdx = addVertex(x, y, undefined, undefined, x2, y2);
                    primitives.push({ type: 'B', p0: prevIdx, p1: newIdx });

                    lastControlX = x2;
                    lastControlY = y2;
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'Q': // Quadratic Bezier
                for (let i = 0; i < values.length; i += 4) {
                    let x1 = values[i];
                    let y1 = values[i + 1];
                    let x = values[i + 2];
                    let y = values[i + 3];

                    if (isRelative) {
                        x1 += currentX; y1 += currentY;
                        x += currentX; y += currentY;
                    }

                    // Convert quadratic to cubic control points
                    const cp1x = currentX + (2/3) * (x1 - currentX);
                    const cp1y = currentY + (2/3) * (y1 - currentY);
                    const cp2x = x + (2/3) * (x1 - x);
                    const cp2y = y + (2/3) * (y1 - y);

                    const prevIdx = vertices.length - 1;
                    // Update previous vertex with outgoing control point (c0)
                    if (prevIdx >= 0) {
                        vertices[prevIdx].c0x = scaleCoord(cp1x, scaleX, offsetX);
                        vertices[prevIdx].c0y = scaleCoord(cp1y, scaleY, offsetY);
                    }

                    // Add new vertex with incoming control point (c1)
                    const newIdx = addVertex(x, y, undefined, undefined, cp2x, cp2y);
                    primitives.push({ type: 'B', p0: prevIdx, p1: newIdx });

                    lastControlX = x1;
                    lastControlY = y1;
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'T': // Smooth Quadratic Bezier
                for (let i = 0; i < values.length; i += 2) {
                    // Control point is reflection of previous
                    let x1 = currentX + (currentX - lastControlX);
                    let y1 = currentY + (currentY - lastControlY);
                    let x = values[i];
                    let y = values[i + 1];

                    if (isRelative) {
                        x += currentX; y += currentY;
                    }

                    // Convert quadratic to cubic
                    const cp1x = currentX + (2/3) * (x1 - currentX);
                    const cp1y = currentY + (2/3) * (y1 - currentY);
                    const cp2x = x + (2/3) * (x1 - x);
                    const cp2y = y + (2/3) * (y1 - y);

                    const prevIdx = vertices.length - 1;
                    // Update previous vertex with outgoing control point (c0)
                    if (prevIdx >= 0) {
                        vertices[prevIdx].c0x = scaleCoord(cp1x, scaleX, offsetX);
                        vertices[prevIdx].c0y = scaleCoord(cp1y, scaleY, offsetY);
                    }

                    // Add new vertex with incoming control point (c1)
                    const newIdx = addVertex(x, y, undefined, undefined, cp2x, cp2y);
                    primitives.push({ type: 'B', p0: prevIdx, p1: newIdx });

                    lastControlX = x1;
                    lastControlY = y1;
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'A': // Arc (convert to bezier approximation)
                // Arc to bezier conversion is complex - for now, approximate with lines
                for (let i = 0; i < values.length; i += 7) {
                    let x = values[i + 5];
                    let y = values[i + 6];

                    if (isRelative) {
                        x += currentX;
                        y += currentY;
                    }

                    // Simple line approximation (TODO: proper arc-to-bezier)
                    const prevIdx = vertices.length - 1;
                    const newIdx = addVertex(x, y);
                    primitives.push({ type: 'L', p0: prevIdx, p1: newIdx });

                    currentX = x;
                    currentY = y;
                }
                break;

            case 'Z': // ClosePath - close to start of CURRENT subpath
                if (vertices.length > subpathStartIdx) {
                    const startVertex = vertices[subpathStartIdx];
                    const epsilon = 0.0001;

                    // Remove ALL duplicate vertices at the start position
                    // This handles cases where multiple commands (C, L, etc.) end at start
                    let foundC1 = false;
                    let c1xToTransfer: number | undefined;
                    let c1yToTransfer: number | undefined;

                    while (vertices.length > subpathStartIdx + 1) {
                        const lastIdx = vertices.length - 1;
                        const lastVertex = vertices[lastIdx];

                        const isSamePosition =
                            Math.abs(lastVertex.x - startVertex.x) < epsilon &&
                            Math.abs(lastVertex.y - startVertex.y) < epsilon;

                        if (!isSamePosition) break;

                        // Collect c1 from the first duplicate that has it (from bezier)
                        if (!foundC1 && lastVertex.c1x !== undefined && lastVertex.c1y !== undefined) {
                            c1xToTransfer = lastVertex.c1x;
                            c1yToTransfer = lastVertex.c1y;
                            foundC1 = true;
                        }

                        // Remove the duplicate vertex
                        vertices.pop();

                        // Remove primitives that reference this removed vertex
                        // and update the previous primitive to point to start
                        while (primitives.length > 0) {
                            const lastPrim = primitives[primitives.length - 1];
                            if (lastPrim.p1 === lastIdx) {
                                // This primitive ended at the removed vertex
                                if (lastPrim.p0 === lastIdx - 1 || lastPrim.p0 >= subpathStartIdx) {
                                    // Update to point to start instead
                                    lastPrim.p1 = subpathStartIdx;
                                    break;
                                } else {
                                    // Primitive from removed vertex - remove it
                                    primitives.pop();
                                }
                            } else if (lastPrim.p0 === lastIdx) {
                                // Primitive from removed vertex - remove it
                                primitives.pop();
                            } else {
                                break;
                            }
                        }
                    }

                    // Transfer the collected c1 to start vertex
                    if (c1xToTransfer !== undefined && c1yToTransfer !== undefined) {
                        startVertex.c1x = c1xToTransfer;
                        startVertex.c1y = c1yToTransfer;
                    }

                    // Check if we still need a closing primitive
                    const lastIdx = vertices.length - 1;
                    if (lastIdx > subpathStartIdx) {
                        // Check if last primitive already closes to start
                        const needsClosing = primitives.length === 0 ||
                            primitives[primitives.length - 1].p1 !== subpathStartIdx;

                        if (needsClosing) {
                            // Use bezier if the last vertex has an outgoing control point (c0)
                            // and start vertex has incoming control point (c1)
                            const lastVertex = vertices[lastIdx];
                            const usesBezier = lastVertex.c0x !== undefined && startVertex.c1x !== undefined;
                            primitives.push({ type: usesBezier ? 'B' : 'L', p0: lastIdx, p1: subpathStartIdx });
                        }
                    }

                    // Finalize this subpath
                    finalizeSubpath();
                }
                currentX = startX;
                currentY = startY;
                break;
        }
    }

    // Finalize any remaining subpath (paths without explicit Z command)
    // Apply the same duplicate removal logic as Z command
    if (vertices.length > 0 && primitives.length > 0) {
        const startVertex = vertices[subpathStartIdx];
        const epsilon = 0.0001;

        // Remove ALL duplicate vertices at the start position
        let foundC1 = false;
        let c1xToTransfer: number | undefined;
        let c1yToTransfer: number | undefined;

        while (vertices.length > subpathStartIdx + 1) {
            const lastIdx = vertices.length - 1;
            const lastVertex = vertices[lastIdx];

            const isSamePosition =
                Math.abs(lastVertex.x - startVertex.x) < epsilon &&
                Math.abs(lastVertex.y - startVertex.y) < epsilon;

            if (!isSamePosition) break;

            // Collect c1 from the first duplicate that has it
            if (!foundC1 && lastVertex.c1x !== undefined && lastVertex.c1y !== undefined) {
                c1xToTransfer = lastVertex.c1x;
                c1yToTransfer = lastVertex.c1y;
                foundC1 = true;
            }

            // Remove the duplicate vertex
            vertices.pop();

            // Remove primitives that reference this removed vertex
            while (primitives.length > 0) {
                const lastPrim = primitives[primitives.length - 1];
                if (lastPrim.p1 === lastIdx) {
                    if (lastPrim.p0 === lastIdx - 1 || lastPrim.p0 >= subpathStartIdx) {
                        lastPrim.p1 = subpathStartIdx;
                        break;
                    } else {
                        primitives.pop();
                    }
                } else if (lastPrim.p0 === lastIdx) {
                    primitives.pop();
                } else {
                    break;
                }
            }
        }

        // Transfer c1 to start vertex
        if (c1xToTransfer !== undefined && c1yToTransfer !== undefined) {
            startVertex.c1x = c1xToTransfer;
            startVertex.c1y = c1yToTransfer;
        }

        // Check if closing primitive is needed
        const lastIdx = vertices.length - 1;
        if (lastIdx > subpathStartIdx) {
            const needsClosing = primitives.length === 0 ||
                primitives[primitives.length - 1].p1 !== subpathStartIdx;

            if (needsClosing) {
                // Use bezier if both endpoints have appropriate control points
                const lastVertex = vertices[lastIdx];
                const usesBezier = lastVertex.c0x !== undefined && startVertex.c1x !== undefined;
                primitives.push({ type: usesBezier ? 'B' : 'L', p0: lastIdx, p1: subpathStartIdx });
            }
        }

        finalizeSubpath();
    }

    return allPaths;
}

// ============================================================
// Main Build Function
// ============================================================

// LightBurn layer color palette (indices)
// Generate complete color-to-layer-index mapping from LIGHTBURN_LAYER_COLORS (C00-C29 + T1, T2)
const LIGHTBURN_COLORS: Record<string, number> = LIGHTBURN_LAYER_COLORS.reduce(
    (acc, color, index) => {
        acc[color.value.toLowerCase()] = index;
        return acc;
    },
    {} as Record<string, number>
);

// Add legacy xTool color aliases for backwards compatibility with old templates
LIGHTBURN_COLORS['#fe0002'] = 2;  // xTool Red -> C02
LIGHTBURN_COLORS['#ff7f56'] = 5;  // xTool Orange -> C05
LIGHTBURN_COLORS['#e1c000'] = 4;  // xTool Yellow -> C04
LIGHTBURN_COLORS['#00c715'] = 3;  // xTool Green -> C03
LIGHTBURN_COLORS['#00befe'] = 6;  // xTool Cyan -> C06
LIGHTBURN_COLORS['#2366ff'] = 1;  // xTool Blue -> C01
LIGHTBURN_COLORS['#a958ff'] = 7;  // xTool Purple -> C07

// Default layer indices (fallback if color not found)
const DEFAULT_LAYER_SCORE = 2;    // Red
const DEFAULT_LAYER_ENGRAVE = 3;  // Green
const DEFAULT_LAYER_CUT = 6;      // Cyan

/**
 * Get LightBurn layer index from a color hex code
 */
function getLayerFromColor(color: string | undefined, defaultLayer: number): number {
    if (!color) return defaultLayer;
    const normalizedColor = color.toLowerCase();
    return LIGHTBURN_COLORS[normalizedColor] ?? defaultLayer;
}

/**
 * Ellipse data extracted from SVG
 */
interface ExtractedEllipse {
    cx: number;
    cy: number;
    rx: number;
    ry: number;
}

/**
 * Result of extracting SVG elements
 */
interface ExtractedSvgData {
    paths: { vertices: LbrnVertex[]; primitives: LbrnPrimitive[] }[];
    ellipses: ExtractedEllipse[];
}

/**
 * Parse SVG transform attribute into a transformation matrix
 * Handles: translate(x,y), scale(x,y), rotate(a), matrix(a,b,c,d,e,f)
 * Returns: { a, b, c, d, e, f } representing the 2D transformation matrix
 */
function parseTransformAttribute(transform: string): { a: number; b: number; c: number; d: number; e: number; f: number } {
    // Start with identity matrix
    let matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

    // Match all transform functions
    const transformRegex = /(translate|scale|rotate|matrix|skewX|skewY)\s*\(([^)]+)\)/gi;
    let match;

    while ((match = transformRegex.exec(transform)) !== null) {
        const type = match[1].toLowerCase();
        const values = match[2].split(/[\s,]+/).map(Number);

        let local = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

        switch (type) {
            case 'translate':
                local.e = values[0] || 0;
                local.f = values[1] || 0;
                break;
            case 'scale':
                local.a = values[0] || 1;
                local.d = values.length > 1 ? values[1] : values[0] || 1;
                break;
            case 'rotate': {
                const angle = (values[0] || 0) * Math.PI / 180;
                const cos = Math.cos(angle);
                const sin = Math.sin(angle);
                // If rotation has cx, cy parameters, translate first
                if (values.length >= 3) {
                    const cx = values[1];
                    const cy = values[2];
                    // Equivalent to: translate(cx,cy) rotate(a) translate(-cx,-cy)
                    local = {
                        a: cos,
                        b: sin,
                        c: -sin,
                        d: cos,
                        e: cx - cx * cos + cy * sin,
                        f: cy - cx * sin - cy * cos,
                    };
                } else {
                    local = { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
                }
                break;
            }
            case 'matrix':
                if (values.length >= 6) {
                    local = { a: values[0], b: values[1], c: values[2], d: values[3], e: values[4], f: values[5] };
                }
                break;
            case 'skewx': {
                const angle = (values[0] || 0) * Math.PI / 180;
                local.c = Math.tan(angle);
                break;
            }
            case 'skewy': {
                const angle = (values[0] || 0) * Math.PI / 180;
                local.b = Math.tan(angle);
                break;
            }
        }

        // Multiply matrices: matrix = matrix * local
        matrix = {
            a: matrix.a * local.a + matrix.c * local.b,
            b: matrix.b * local.a + matrix.d * local.b,
            c: matrix.a * local.c + matrix.c * local.d,
            d: matrix.b * local.c + matrix.d * local.d,
            e: matrix.a * local.e + matrix.c * local.f + matrix.e,
            f: matrix.b * local.e + matrix.d * local.f + matrix.f,
        };
    }

    return matrix;
}

/**
 * Collect all transforms from an element up to the SVG root and compose them
 * Returns the composed transformation matrix
 */
function getComposedTransform(element: Element, svgRoot: Element): { a: number; b: number; c: number; d: number; e: number; f: number } {
    const transforms: { a: number; b: number; c: number; d: number; e: number; f: number }[] = [];
    let current: Element | null = element.parentElement;

    // Collect transforms from parent elements up to (but not including) svgRoot
    while (current && current !== svgRoot) {
        const transform = current.getAttribute('transform');
        if (transform) {
            transforms.unshift(parseTransformAttribute(transform)); // Add to front (outermost first)
        }
        current = current.parentElement;
    }

    // Compose all transforms: result = transform1 * transform2 * ... * transformN
    let result = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }; // Identity
    for (const t of transforms) {
        result = {
            a: result.a * t.a + result.c * t.b,
            b: result.b * t.a + result.d * t.b,
            c: result.a * t.c + result.c * t.d,
            d: result.b * t.c + result.d * t.d,
            e: result.a * t.e + result.c * t.f + result.e,
            f: result.b * t.e + result.d * t.f + result.f,
        };
    }

    return result;
}

/**
 * Extract SVG paths, circles, and ellipses from SVG content and convert to LBRN format
 */
function extractSvgPathsToLbrn(
    svgData: string,
    shapeX: number,
    shapeY: number,
    shapeWidth: number,
    shapeHeight: number
): ExtractedSvgData {
    const result: ExtractedSvgData = { paths: [], ellipses: [] };

    try {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgData, 'image/svg+xml');
        const svgElem = svgDoc.querySelector('svg');

        if (!svgElem) return result;

        // Parse viewBox
        let viewBoxX = 0, viewBoxY = 0, viewBoxWidth = shapeWidth, viewBoxHeight = shapeHeight;
        const viewBox = svgElem.getAttribute('viewBox');
        if (viewBox) {
            const parts = viewBox.split(/[\s,]+/).map(Number);
            if (parts.length === 4) {
                [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] = parts;
            }
        }

        // Calculate scale maintaining aspect ratio
        const scaleX = shapeWidth / viewBoxWidth;
        const scaleY = shapeHeight / viewBoxHeight;
        const scale = Math.min(scaleX, scaleY);

        // Center graphic if aspect ratios don't match
        const scaledWidth = viewBoxWidth * scale;
        const scaledHeight = viewBoxHeight * scale;
        const offsetX = shapeX + (shapeWidth - scaledWidth) / 2 - viewBoxX * scale;
        const offsetY = shapeY + (shapeHeight - scaledHeight) / 2 - viewBoxY * scale;

        // Extract all path elements
        const pathElements = svgElem.querySelectorAll('path');
        pathElements.forEach(pathElem => {
            const d = pathElem.getAttribute('d');
            if (!d) return;

            // Get composed transform from parent groups AND the path element itself
            const groupTransform = getComposedTransform(pathElem, svgElem);

            // Check if the path element has its own transform attribute
            const pathTransform = pathElem.getAttribute('transform');
            let finalTransform = groupTransform;
            if (pathTransform) {
                const localTransform = parseTransformAttribute(pathTransform);
                // Compose: group * local
                finalTransform = {
                    a: groupTransform.a * localTransform.a + groupTransform.c * localTransform.b,
                    b: groupTransform.b * localTransform.a + groupTransform.d * localTransform.b,
                    c: groupTransform.a * localTransform.c + groupTransform.c * localTransform.d,
                    d: groupTransform.b * localTransform.c + groupTransform.d * localTransform.d,
                    e: groupTransform.a * localTransform.e + groupTransform.c * localTransform.f + groupTransform.e,
                    f: groupTransform.b * localTransform.e + groupTransform.d * localTransform.f + groupTransform.f,
                };
            }

            // Apply group transform to scale and offset
            // Group transforms are in SVG space, then viewBox scale is applied
            // Order: localCoord -> groupTransform -> viewBoxScale -> output
            // Combined: x' = (a*x + c*y + e) * scale + offset
            //         = a*scale*x + c*scale*y + e*scale + offset
            const combinedScaleX = scale * finalTransform.a;
            const combinedScaleY = scale * finalTransform.d;
            const combinedOffsetX = offsetX + finalTransform.e * scale;
            const combinedOffsetY = offsetY + finalTransform.f * scale;

            // svgPathToLbrnPaths returns an array of subpaths (one per closed contour)
            const subpaths = svgPathToLbrnPaths(d, combinedOffsetX, combinedOffsetY, combinedScaleX, combinedScaleY);
            for (const pathData of subpaths) {
                if (pathData.vertices.length > 0) {
                    result.paths.push(pathData);
                }
            }
        });

        // Helper to get element's final transform (group + element's own)
        const getElementTransform = (elem: Element) => {
            const groupTransform = getComposedTransform(elem, svgElem);
            const elemTransformAttr = elem.getAttribute('transform');
            if (!elemTransformAttr) return groupTransform;

            const localTransform = parseTransformAttribute(elemTransformAttr);
            return {
                a: groupTransform.a * localTransform.a + groupTransform.c * localTransform.b,
                b: groupTransform.b * localTransform.a + groupTransform.d * localTransform.b,
                c: groupTransform.a * localTransform.c + groupTransform.c * localTransform.d,
                d: groupTransform.b * localTransform.c + groupTransform.d * localTransform.d,
                e: groupTransform.a * localTransform.e + groupTransform.c * localTransform.f + groupTransform.e,
                f: groupTransform.b * localTransform.e + groupTransform.d * localTransform.f + groupTransform.f,
            };
        };

        // Extract all circle elements
        const circleElements = svgElem.querySelectorAll('circle');
        circleElements.forEach(circleElem => {
            const cx = parseFloat(circleElem.getAttribute('cx') || '0');
            const cy = parseFloat(circleElem.getAttribute('cy') || '0');
            const r = parseFloat(circleElem.getAttribute('r') || '0');

            if (r > 0) {
                const transform = getElementTransform(circleElem);
                // Apply transform to center point
                const transformedCx = (transform.a * cx + transform.c * cy + transform.e) * scale + offsetX;
                const transformedCy = (transform.b * cx + transform.d * cy + transform.f) * scale + offsetY;
                // Scale radius by the average of scaleX and scaleY from transform
                const transformedRx = r * Math.abs(transform.a) * scale;
                const transformedRy = r * Math.abs(transform.d) * scale;

                result.ellipses.push({
                    cx: transformedCx,
                    cy: transformedCy,
                    rx: transformedRx,
                    ry: transformedRy,
                });
            }
        });

        // Extract all ellipse elements
        const ellipseElements = svgElem.querySelectorAll('ellipse');
        ellipseElements.forEach(ellipseElem => {
            const cx = parseFloat(ellipseElem.getAttribute('cx') || '0');
            const cy = parseFloat(ellipseElem.getAttribute('cy') || '0');
            const rx = parseFloat(ellipseElem.getAttribute('rx') || '0');
            const ry = parseFloat(ellipseElem.getAttribute('ry') || '0');

            if (rx > 0 && ry > 0) {
                const transform = getElementTransform(ellipseElem);
                // Apply transform to center point
                const transformedCx = (transform.a * cx + transform.c * cy + transform.e) * scale + offsetX;
                const transformedCy = (transform.b * cx + transform.d * cy + transform.f) * scale + offsetY;
                // Scale radii by transform scale components
                const transformedRx = rx * Math.abs(transform.a) * scale;
                const transformedRy = ry * Math.abs(transform.d) * scale;

                result.ellipses.push({
                    cx: transformedCx,
                    cy: transformedCy,
                    rx: transformedRx,
                    ry: transformedRy,
                });
            }
        });

        return result;
    } catch (e) {
        console.warn('Failed to parse SVG:', e);
        return result;
    }
}

/**
 * Builds an LBRN file from shapes and fields
 * This is the main entry point, matching buildXcs() signature
 */
export async function buildLbrn(
    shapes: TemplateShape[],
    fields: ExtendedField[],
    values: Record<string, string>,
    _width: number,
    _height: number,
    options: LbrnBuildOptions = {}
): Promise<string> {
    const {
        // IMPORTANT: LightBurn always stores speeds in mm/s regardless of machine type.
        // Default to 'dsp' controller to avoid 60× speed multiplier from 'grbl' conversion.
        controllerType = 'dsp',
        processSettings,
        fonts = [],  // Used for glyph-based font size calculation
        mirrorX = false,
        mirrorY = true, // Default true for bottom-left origin (matches xTool)
    } = options;

    // Build XML header
    const header = `<?xml version="1.0" encoding="UTF-8"?>`;

    // Project attributes - match working LightBurn file format
    const projectAttrs: Record<string, string | number> = {
        AppVersion: LBRN_VERSION,
        FormatVersion: LBRN_FORMAT_VERSION,
        MaterialHeight: 0,
        MirrorX: mirrorX ? 'True' : 'False',
        MirrorY: mirrorY ? 'True' : 'False',
    };

    // Collect CutSettings and Shapes
    const cutSettingsMap = new Map<number, LbrnCutSetting>();
    const shapeElements: string[] = [];

    // Helper to get or create cut setting for a layer
    const getOrCreateCutSetting = (layerIndex: number, isEngrave: boolean, shapeSettings?: ShapeProcessSettings, isImage?: boolean): LbrnCutSetting => {
        if (cutSettingsMap.has(layerIndex)) {
            return cutSettingsMap.get(layerIndex)!;
        }

        let setting: LbrnCutSetting;
        if (isImage) {
            // Image type for bitmap raster engraving
            setting = createImageCutSetting(layerIndex, {
                power: shapeSettings?.power ?? 20,
                speed: shapeSettings?.speed ?? 100,
                controllerType,
            });
        } else if (isEngrave) {
            setting = createScanCutSetting(layerIndex, {
                power: shapeSettings?.power ?? 30,
                speed: shapeSettings?.speed ?? 200,
                density: shapeSettings?.density ?? 100,
                controllerType,
                bidir: shapeSettings?.scanMode !== 'oneWay',
            });
        } else {
            setting = createVectorCutSetting(layerIndex, {
                power: shapeSettings?.power ?? (layerIndex === DEFAULT_LAYER_CUT ? 80 : 20),
                speed: shapeSettings?.speed ?? (layerIndex === DEFAULT_LAYER_CUT ? 50 : 100),
                passes: shapeSettings?.passes ?? 1,
                controllerType,
                isCut: layerIndex === DEFAULT_LAYER_CUT,
            });
        }

        // Merge LightBurn-specific settings from process settings
        const lbrnSettings = mapProcessSettingsToLbrn(shapeSettings, isEngrave);
        setting = { ...setting, ...lbrnSettings };

        cutSettingsMap.set(layerIndex, setting);
        return setting;
    };

    // Sort shapes by size (larger first, like XCS)
    const sortedShapes = [...shapes].sort((a, b) => {
        const areaA = a.width * a.height;
        const areaB = b.width * b.height;
        return areaB - areaA;
    });

    // Process each shape
    for (const shape of sortedShapes) {
        // Get process settings for this shape
        const shapeSettingsMap = processSettings?.get(shape.id);
        const scoreSettings = shapeSettingsMap?.get('score');
        const engraveSettings = shapeSettingsMap?.get('engrave');
        const cutSettings = shapeSettingsMap?.get('cut');

        // Determine which operations are enabled
        const useScore = shape.score ?? false;
        const useEngrave = shape.engrave !== false; // Default true
        const useCut = shape.cut ?? false;

        // Get layer indices:
        // 1. If lbrnCutIndex is set (from LBRN import), use it for round-trip fidelity
        // 2. Otherwise fall back to color-based mapping (configured in Template Editor)
        const scoreLayer = shape.lbrnCutIndex ?? getLayerFromColor(shape.scoreColor, DEFAULT_LAYER_SCORE);
        const engraveLayer = shape.lbrnCutIndex ?? getLayerFromColor(shape.engraveColor, DEFAULT_LAYER_ENGRAVE);
        const cutLayer = shape.lbrnCutIndex ?? getLayerFromColor(shape.cutColor, DEFAULT_LAYER_CUT);

        if (shape.type === 'graphic' && shape.svgData) {
            // Handle SVG graphics (monograms, decorative elements)
            const extractedData = extractSvgPathsToLbrn(
                shape.svgData,
                shape.x,
                shape.y,
                shape.width,
                shape.height
            );

            // Process path elements
            for (const pathData of extractedData.paths) {
                if (pathData.vertices.length === 0) continue;

                // Identity transform for path shapes (required by LightBurn)
                const identityXform = '1 0 0 1 0 0';

                // Create path for each enabled operation
                if (useScore) {
                    getOrCreateCutSetting(scoreLayer, false, scoreSettings);
                    const pathXml = createPathShape(pathData.vertices, pathData.primitives, scoreLayer, identityXform);
                    shapeElements.push(pathXml);
                }

                if (useEngrave) {
                    getOrCreateCutSetting(engraveLayer, true, engraveSettings);
                    const pathXml = createPathShape(pathData.vertices, pathData.primitives, engraveLayer, identityXform);
                    shapeElements.push(pathXml);
                }

                if (useCut) {
                    getOrCreateCutSetting(cutLayer, false, cutSettings);
                    const pathXml = createPathShape(pathData.vertices, pathData.primitives, cutLayer, identityXform);
                    shapeElements.push(pathXml);
                }
            }

            // Process circle and ellipse elements
            for (const ellipse of extractedData.ellipses) {
                // Create ellipse for each enabled operation
                if (useScore) {
                    getOrCreateCutSetting(scoreLayer, false, scoreSettings);
                    const ellipseXml = createEllipseShape(ellipse.cx, ellipse.cy, ellipse.rx, ellipse.ry, scoreLayer);
                    shapeElements.push(ellipseXml);
                }

                if (useEngrave) {
                    getOrCreateCutSetting(engraveLayer, true, engraveSettings);
                    const ellipseXml = createEllipseShape(ellipse.cx, ellipse.cy, ellipse.rx, ellipse.ry, engraveLayer);
                    shapeElements.push(ellipseXml);
                }

                if (useCut) {
                    getOrCreateCutSetting(cutLayer, false, cutSettings);
                    const ellipseXml = createEllipseShape(ellipse.cx, ellipse.cy, ellipse.rx, ellipse.ry, cutLayer);
                    shapeElements.push(ellipseXml);
                }
            }
        } else if (shape.type === 'rect') {
            // Handle rectangle shapes
            if (shape.fieldId) {
                // Text rectangles - render text content using glyph-based approach
                const field = fields.find(f => f.id === shape.fieldId);
                if (!field) continue;

                const value = values[shape.fieldId];
                if (!value) continue;

                // Skip internal variation selector field
                if (field.label === '__variation_selector__') continue;

                const fontFamily = field.fontFamily || 'Arial';

                // Convert fonts to textGenerator format for glyph-based measurement
                const textGenFonts: TextGenFont[] = fonts
                    .filter(f => f.url)
                    .map(f => ({
                        id: f.id,
                        name: f.name,
                        family: f.family,
                        url: f.url!
                    }));

                // Calculate optimal fontSize AND scaled bounds for accurate positioning
                // This ensures text fills the shape properly AND is positioned correctly
                // regardless of ascenders/descenders in the text
                const { fontSize, scaledBounds } = await calculateTextFitInfo(
                    value,
                    fontFamily,
                    textGenFonts,
                    { x: shape.x, y: shape.y, width: shape.width, height: shape.height },
                    ROBOTO_REGULAR_BASE64  // Fallback font
                );

                // Use SHAPE's score/engrave settings (configured in Template Editor)
                // Shape settings are what users explicitly configure for each zone
                // Layer indices determined by shape colors
                // Use LightBurn native Text shape with Weld="1" for proper text welding
                if (useScore) {
                    getOrCreateCutSetting(scoreLayer, false, scoreSettings);
                    const textXml = createTextShape(value, shape.x, shape.y, scoreLayer, {
                        fontFamily,
                        width: shape.width,
                        height: shape.height,
                        fontSize,
                        scaledBounds
                    });
                    shapeElements.push(textXml);
                }

                if (useEngrave) {
                    getOrCreateCutSetting(engraveLayer, true, engraveSettings);
                    const textXml = createTextShape(value, shape.x, shape.y, engraveLayer, {
                        fontFamily,
                        width: shape.width,
                        height: shape.height,
                        fontSize,
                        scaledBounds
                    });
                    shapeElements.push(textXml);
                }
            } else {
                // Pure rectangle shapes (no fieldId) - render as geometric rectangles
                // Create rect for each enabled operation
                if (useScore) {
                    getOrCreateCutSetting(scoreLayer, false, scoreSettings);
                    const rectXml = createRectShape(shape.x, shape.y, shape.width, shape.height, scoreLayer);
                    shapeElements.push(rectXml);
                }

                if (useEngrave) {
                    getOrCreateCutSetting(engraveLayer, true, engraveSettings);
                    const rectXml = createRectShape(shape.x, shape.y, shape.width, shape.height, engraveLayer);
                    shapeElements.push(rectXml);
                }

                if (useCut) {
                    getOrCreateCutSetting(cutLayer, false, cutSettings);
                    const rectXml = createRectShape(shape.x, shape.y, shape.width, shape.height, cutLayer);
                    shapeElements.push(rectXml);
                }
            }
        } else if (shape.type === 'raster' && shape.rasterData) {
            // Handle raster images - rasters only support engrave operation
            // Rasters always use engrave (Image type CutSetting)
            if (useEngrave) {
                // Get image settings preserved from LBRN import for round-trip
                const imageSettings = (shape.originalXcsData as Record<string, unknown> | undefined)?.lbrnImageSettings as
                    { gamma?: number; contrast?: number; brightness?: number } | undefined;

                // Create CutSetting_Img for this layer (Image type for bitmap engraving)
                getOrCreateCutSetting(engraveLayer, true, engraveSettings, true);  // isImage=true

                const bitmapXml = createBitmapShape(
                    shape.rasterData,
                    shape.x,
                    shape.y,
                    shape.width,
                    shape.height,
                    shape.rasterOriginalWidth || Math.round(shape.width),
                    shape.rasterOriginalHeight || Math.round(shape.height),
                    engraveLayer,
                    imageSettings
                );
                shapeElements.push(bitmapXml);
            }
        }
    }

    // Ensure we have at least default cut settings if no shapes used them
    if (cutSettingsMap.size === 0) {
        getOrCreateCutSetting(DEFAULT_LAYER_SCORE, false);
        getOrCreateCutSetting(DEFAULT_LAYER_ENGRAVE, true);
        getOrCreateCutSetting(DEFAULT_LAYER_CUT, false);
    }

    // Sort cut settings by index and serialize
    const sortedCutSettings = Array.from(cutSettingsMap.values())
        .sort((a, b) => a.index - b.index)
        .map(serializeCutSetting);

    // Build final XML
    const projectContent = [
        '',
        '    <!-- CutSettings (Layer Definitions) -->',
        ...sortedCutSettings.map(s => '    ' + s),
        '',
        '    <!-- Shapes -->',
        ...shapeElements.map(s => '    ' + s),
        '',
    ].join('\n');

    const projectElement = xmlElement('LightBurnProject', projectAttrs, projectContent);

    return header + '\n' + projectElement;
}

// ============================================================
// Utility Exports
// ============================================================
// PathCommand is now re-exported at the top of the file from pathUtils.ts
